/**
 * Optimization rule store (Zustand). Wraps `ruleService` with CRUD + toggle and
 * keeps the in-memory list in sync with the DTOs the server returns.
 */
import { create } from 'zustand';
import * as ruleService from '@/services/ruleService';
import type { RuleListParams } from '@/services/ruleService';
import { ApiRequestError } from '@/utils/api';
import type {
  CreateRuleInput,
  OptimizationRuleDTO,
  UpdateRuleInput,
} from '@shared/types';

/** Rule store state + actions. */
export interface RuleState {
  rules: OptimizationRuleDTO[];
  loading: boolean;
  error: string | null;
  fetch: (params?: RuleListParams) => Promise<void>;
  create: (input: CreateRuleInput) => Promise<OptimizationRuleDTO>;
  update: (id: string, input: UpdateRuleInput) => Promise<OptimizationRuleDTO>;
  remove: (id: string) => Promise<void>;
  toggle: (id: string, isActive: boolean) => Promise<OptimizationRuleDTO>;
  clearError: () => void;
}

/** Extract a user-facing message from an unknown thrown value. */
function toMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export const useRuleStore = create<RuleState>()((set) => ({
  rules: [],
  loading: false,
  error: null,

  fetch: async (params) => {
    set({ loading: true, error: null });
    try {
      const rules = await ruleService.list(params);
      set({ rules, loading: false });
    } catch (err) {
      set({ loading: false, error: toMessage(err, 'Không thể tải quy tắc') });
      throw err;
    }
  },

  create: async (input) => {
    set({ error: null });
    try {
      const created = await ruleService.create(input);
      set((state) => ({ rules: [created, ...state.rules] }));
      return created;
    } catch (err) {
      set({ error: toMessage(err, 'Tạo quy tắc thất bại') });
      throw err;
    }
  },

  update: async (id, input) => {
    set({ error: null });
    try {
      const updated = await ruleService.update(id, input);
      set((state) => ({
        rules: state.rules.map((r) => (r.id === id ? updated : r)),
      }));
      return updated;
    } catch (err) {
      set({ error: toMessage(err, 'Cập nhật quy tắc thất bại') });
      throw err;
    }
  },

  remove: async (id) => {
    set({ error: null });
    try {
      await ruleService.remove(id);
      set((state) => ({ rules: state.rules.filter((r) => r.id !== id) }));
    } catch (err) {
      set({ error: toMessage(err, 'Xóa quy tắc thất bại') });
      throw err;
    }
  },

  toggle: async (id, isActive) => {
    set({ error: null });
    try {
      const updated = await ruleService.toggle(id, isActive);
      set((state) => ({
        rules: state.rules.map((r) => (r.id === id ? updated : r)),
      }));
      return updated;
    } catch (err) {
      set({ error: toMessage(err, 'Thay đổi trạng thái quy tắc thất bại') });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
